import { FC, JSX, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import type { Content } from '@/entities/content/api/types';
import Button from '@/shared/ui/button';
import PlusIcon from '@/shared/ui/icons/Plus';

import CreateTextBlockForm from './CreateTextBlockForm';
import TextBlockElement from './TextBlock';

interface ITextBlocksProps {}

const TextBlocks: FC<ITextBlocksProps> = (): JSX.Element => {
  const {
    control,
    formState: { errors },
  } = useFormContext<Content>();
  const [showCreateTextBlockForm, setShowCreateTextBlockForm] = useState(false);
  const [selectedEditIdx, setSelectedEditIdx] = useState<number>(-1);

  return (
    <div className="flex flex-col justify-start gap-2">
      <Controller
        control={control}
        name="text_blocks"
        render={({ field: { value, onChange } }) => (
          <>
            <div className="text-base font-semibold leading-6">Text blocks</div>
            {showCreateTextBlockForm && (
              <CreateTextBlockForm
                textBlock={selectedEditIdx >= 0 ? value[selectedEditIdx] : null}
                onRemove={() => {
                  const nextValue = value.filter(
                    (_, idx) => idx !== selectedEditIdx
                  );

                  setShowCreateTextBlockForm(false);
                  setSelectedEditIdx(-1);
                  onChange(nextValue);
                }}
                onSave={(newTextBlock) => {
                  const nextValue = value.map((tb, idx) =>
                    idx === selectedEditIdx ? newTextBlock : tb
                  );

                  if (selectedEditIdx === -1) {
                    nextValue.push(newTextBlock);
                  }

                  setShowCreateTextBlockForm(false);
                  setSelectedEditIdx(-1);
                  onChange(nextValue);
                }}
              />
            )}
            <div className="flex flex-wrap gap-2">
              {value.map((block, index) => (
                <TextBlockElement
                  index={index + 1}
                  key={block.start + block.end + (block.url ?? '')}
                  data={block}
                  triggerEdit={() => {
                    setSelectedEditIdx(index);
                    setShowCreateTextBlockForm(true);
                  }}
                />
              ))}

              <Button
                variant="function"
                size="sm"
                title="Add text"
                iconAfter={<PlusIcon />}
                disabled={showCreateTextBlockForm}
                onClick={() => setShowCreateTextBlockForm(true)}
                className="rounded-lg border border-dashed border-blue/[.12] bg-blue/[.02] px-3 py-2"
              />
            </div>
          </>
        )}
      />
      {
        // @ts-ignore
        errors?.text_blocks?.text_blocks?.message && (
          <div className="text-sm font-light text-red">
            {
              // @ts-ignore
              errors?.text_blocks?.text_blocks?.message
            }
          </div>
        )
      }
    </div>
  );
};

export default TextBlocks;
